// ============================================================================
// components/block/liveStreamManagment/MixerStrip.tsx
//
// Une voie du Mixeur (Switch audio, mode "Mix") : le même panneau vertical que
// « Diffusion du flux final » et « Intercomm » (HlsAudioPanel) — VU-mètres L/R,
// fader T-Bar, gain en dB — en mode « voie de mixeur » : il ne touche à aucun
// nœud audio, il lit le niveau de la source et remonte gain/mute au parent.
//
// Mute : coupe le son SANS toucher au niveau du fader, simplement ignoré.
// Allumé en vert quand il est actif.
//
// Remis à la verticale le 2026-10-18 à la demande de l'utilisateur, après deux jours
// de voies horizontales (fader du panneau de réglage d'une source) : l'échelle dB de
// HlsAudioPanel (chiffres de 12 px, 0 et +6 en couleur, lignes sur toute la voie) se lit
// bien. La version horizontale est gardée en MixerStrip.tsx.bak-2026-10-18-horizontal.
// ============================================================================

import React, { useRef } from 'react';
import type { AudioSourceRegistry } from '../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';
import HlsAudioPanel, { gainToSlider } from './HlsAudioPanel';

interface MixerStripProps {
  label    : string;
  /** Id de la source du registry (slot du deck ou source hors deck) : lecture du niveau seule. */
  sourceId : string;
  registry?: AudioSourceRegistry;
  /** Gain linéaire (1 = 0 dB) à l'ouverture, conservé même voie coupée. */
  gain     : number;
  muted    : boolean;
  onGainChange : (gain: number) => void;
  onMutedChange: (muted: boolean) => void;
}

const MixerStrip: React.FC<MixerStripProps> = ({ label, sourceId, registry, gain, muted, onGainChange, onMutedChange }) => {
  // Valeur initiale figée : le panneau est ensuite la référence du fader. Le
  // renvoyer à chaque changement ferait un aller-retour gain ↔ slider inutile.
  const initialSlider = useRef(gainToSlider(gain));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: 120, flexShrink: 0 }}>
      <span title={label} style={{
        fontSize: 11, fontWeight: 700, textAlign: 'center', marginBottom: 6,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
      <div style={{ height: 380, display: 'flex' }}>
        <HlsAudioPanel
          meterSourceId={sourceId}
          meterRegistry={registry}
          externalVolume={initialSlider.current}
          externalMuted={muted}
          onGainChange={onGainChange}
          onMutedChange={onMutedChange}
        />
      </div>
    </div>
  );
};

export default MixerStrip;
